import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Workforce, isEndingSoon, getDaysUntilEnd } from "@/types/workforce";

interface WorkforceStats {
	total: number;
	active: number;
	onHold: number;
	completed: number;
	terminated: number;
	endingSoon: number; // Within 30 days
	endingThisWeek: number; // Within 7 days
	endingToday: number;
}

export interface UseWorkforceStatsReturn {
	stats: WorkforceStats;
	isLoading: boolean;
	refetch: () => Promise<void>;
}

const emptyStats: WorkforceStats = {
	total: 0,
	active: 0,
	onHold: 0,
	completed: 0,
	terminated: 0,
	endingSoon: 0,
	endingThisWeek: 0,
	endingToday: 0
};

export const useWorkforceStats = (): UseWorkforceStatsReturn => {
	const [stats, setStats] = useState<WorkforceStats>(emptyStats);
	const [isLoading, setIsLoading] = useState(true);
	const [orgUuid, setOrgUuid] = useState<string | null>(null);

	// Fetch organization UUID once on mount
	useEffect(() => {
		const fetchOrgUuid = async () => {
			try {
				const { data: { user } } = await supabase.auth.getUser();
				if (!user) return;

				const { data: userOrg } = await supabase
					.from('user_organization')
					.select('organization_uuid')
					.eq('user_uuid', user.id)
					.single();

				if (userOrg?.organization_uuid) {
					setOrgUuid(userOrg.organization_uuid);
				}
			} catch (error) {
				// Silent error - stats will show empty
			}
		};

		fetchOrgUuid();
	}, []);

	const fetchStats = useCallback(async () => {
		if (!orgUuid) return;

		try {
			setIsLoading(true);

			// Fetch all non-bench workforce entries to calculate stats
			const { data, error } = await supabase
				.from('workforce')
				.select('id, status, end_date, expected_end_date, is_bench')
				.eq('organization_uuid', orgUuid)
				.neq('is_bench', true);

			if (error) throw error;

			const workforceData = data || [];

			// Calculate status counts
			const statusCounts = workforceData.reduce((acc, item) => {
				acc[item.status] = (acc[item.status] || 0) + 1;
				return acc;
			}, {} as Record<string, number>);

			// Calculate ending soon counts (only for active assignments)
			const activeItems = workforceData.filter(item => item.status === 'active');

			let endingSoon = 0;
			let endingThisWeek = 0;
			let endingToday = 0;

			activeItems.forEach(item => {
				const endDate = item.expected_end_date || item.end_date;
				const daysUntil = getDaysUntilEnd(endDate);

				if (daysUntil !== null && daysUntil >= 0) {
					if (daysUntil <= 30) endingSoon++;
					if (daysUntil <= 7) endingThisWeek++;
					if (daysUntil === 0) endingToday++;
				}
			});

			setStats({
				total: workforceData.length,
				active: statusCounts['active'] || 0,
				onHold: statusCounts['on_hold'] || 0,
				completed: statusCounts['completed'] || 0,
				terminated: statusCounts['terminated'] || 0,
				endingSoon,
				endingThisWeek,
				endingToday
			});
		} catch (error) {
			setStats(emptyStats);
		} finally {
			setIsLoading(false);
		}
	}, [orgUuid]);

	useEffect(() => {
		fetchStats();
	}, [fetchStats]);

	return {
		stats,
		isLoading,
		refetch: fetchStats
	};
};
